import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ticket, Percent, KeyRound, Link2, MessageCircle, Send } from "lucide-react";
import { api, formatError, fmtDate, sgtToISO, isoToSgtLocal } from "../../lib/api";
import { AdminHeader, AdminModal, AField } from "../../components/AdminLayout";

const BLANK = { code: "", kind: "special", discount_percent: 10, expires_at: "", active: true, description: "", unlock_package_ids: [] };

export default function AdminCodes({ embedded = false, kindFilter = "all" }) {
  const [items, setItems] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setItems(r.data)).catch(() => {});
  const loadPkgs = () => api.get("/admin/packages").then((r) => setPkgs(r.data)).catch(() => {});
  useEffect(() => { load(); loadPkgs(); }, []);

  const openNew = () => setModal({ ...BLANK });
  const openEdit = (c) => setModal({
    ...c, expires_at: isoToSgtLocal(c.expires_at),
    unlock_package_ids: pkgs.filter((p) => (p.unlock_code || "").toUpperCase() === (c.code || "").toUpperCase()).map((p) => p.id),
  });

  const save = async () => {
    setBusy(true);
    const payload = {
      code: modal.code, kind: modal.kind,
      discount_percent: modal.kind === "promotion" ? parseInt(modal.discount_percent, 10) || 0 : 0,
      expires_at: modal.expires_at ? sgtToISO(modal.expires_at) : null,
      active: !!modal.active, description: modal.description,
    };
    if (modal.kind === "special") payload.unlock_package_ids = modal.unlock_package_ids || [];
    try {
      if (modal.id) await api.put(`/admin/promo-codes/${modal.id}`, payload);
      else await api.post("/admin/promo-codes", payload);
      toast.success("Code saved"); setModal(null); load(); loadPkgs();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete code ${c.code}?`)) return;
    try { await api.delete(`/admin/promo-codes/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const shareLink = (c) => `${window.location.origin}/packages?code=${encodeURIComponent(c.code)}`;
  const copyLink = async (c) => {
    const link = shareLink(c);
    try { await navigator.clipboard.writeText(link); toast.success("Share link copied", { description: link }); }
    catch { toast.message(link); }
  };
  const shareWa = (c) => window.open(`https://wa.me/?text=${encodeURIComponent(`Unlock exclusive Darkchata packages with code ${c.code}: ${shareLink(c)}`)}`, "_blank");
  const shareTg = (c) => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareLink(c))}&text=${encodeURIComponent(`Unlock exclusive Darkchata packages with code ${c.code}`)}`, "_blank");

  const shown = items.filter((c) => kindFilter === "all" || c.kind === kindFilter);
  return (
    <div>
      {embedded ? (
        <div className="flex justify-end mb-5"><button onClick={openNew} data-testid="admin-add-code" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New code</button></div>
      ) : (
        <AdminHeader title="Promo & special codes" subtitle="Special codes unlock hidden packages · Promotion codes give a % discount"
          action={<button onClick={openNew} data-testid="admin-add-code" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New code</button>} />
      )}

      {shown.length === 0 ? (
        <p className="text-[#A19DB0] text-sm">No codes yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {shown.map((c) => (
            <div key={c.id} data-testid={`code-${c.code}`} className={`rounded-2xl border p-5 ${c.active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-60"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#9D4EDD]/20 flex items-center justify-center">
                    {c.kind === "promotion" ? <Percent size={16} className="text-[#D4AF37]" /> : <KeyRound size={16} className="text-[#D4AF37]" />}
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold tracking-wide">{c.code}</h3>
                    <p className="text-[#A19DB0] text-xs">{c.kind === "promotion" ? `${c.discount_percent}% discount` : "Unlocks hidden packages"}</p>
                  </div>
                </div>
                {!c.active && <span className="text-[10px] uppercase text-[#E57373]">inactive</span>}
              </div>
              {c.description && <p className="text-[#A19DB0] text-sm mt-2">{c.description}</p>}
              <p className="text-[#A19DB0]/70 text-xs mt-2">{c.expires_at ? `Expires ${fmtDate(c.expires_at)}` : "No expiry"}</p>
              <div className="flex items-center gap-3 text-xs text-[#A19DB0] mt-3" data-testid={`code-stats-${c.code}`}>
                <span>{c.link_visits || 0} link visits</span>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span>{c.link_orders || 0} orders</span>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span className="text-[#D4AF37] font-semibold" data-testid={`code-conv-${c.code}`}>{c.link_visits ? Math.round(((c.link_orders || 0) / c.link_visits) * 100) : 0}% conv.</span>
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                <button onClick={() => copyLink(c)} data-testid={`copy-link-${c.code}`} className="flex items-center gap-1.5 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-3 py-1.5 text-sm hover:bg-[#D4AF37]/20"><Link2 size={14} /> Copy link</button>
                <button onClick={() => shareWa(c)} data-testid={`share-wa-${c.code}`} className="flex items-center gap-1.5 bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/30 rounded-full px-3 py-1.5 text-sm hover:bg-[#25D366]/20"><MessageCircle size={14} /> WhatsApp</button>
                <button onClick={() => shareTg(c)} data-testid={`share-tg-${c.code}`} className="flex items-center gap-1.5 bg-[#229ED9]/10 text-[#229ED9] border border-[#229ED9]/30 rounded-full px-3 py-1.5 text-sm hover:bg-[#229ED9]/20"><Send size={14} /> Telegram</button>
                <button onClick={() => openEdit(c)} data-testid={`edit-code-${c.code}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><Pencil size={14} /> Edit</button>
                <button onClick={() => del(c)} data-testid={`delete-code-${c.code}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm text-[#E57373] hover:bg-white/10"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <AdminModal open onOpenChange={() => setModal(null)} title={modal.id ? "Edit code" : "New code"} onSubmit={save} busy={busy}>
          <AField label="Code" value={modal.code} onChange={(e) => setModal({ ...modal, code: e.target.value.toUpperCase() })} data-testid="code-value" />
          <div>
            <label className="text-xs text-[#A19DB0]">Type</label>
            <select value={modal.kind} onChange={(e) => setModal({ ...modal, kind: e.target.value })} data-testid="code-kind" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]">
              <option value="special" className="bg-[#140F1E]">Special — unlocks hidden packages</option>
              <option value="promotion" className="bg-[#140F1E]">Promotion — % discount</option>
            </select>
          </div>
          {modal.kind === "promotion" && (
            <AField label="Discount %" type="number" value={modal.discount_percent} onChange={(e) => setModal({ ...modal, discount_percent: e.target.value })} data-testid="code-discount" />
          )}
          {modal.kind === "special" && (
            <div>
              <label className="text-xs text-[#A19DB0]">Hidden packages this code unlocks</label>
              <div data-testid="code-packages" className="mt-1 max-h-52 overflow-y-auto rounded-xl border border-white/10 bg-white/5 p-2 space-y-1">
                {pkgs.length === 0 ? <p className="text-[#A19DB0] text-xs p-2">No packages yet — create packages first.</p> : pkgs.map((p) => {
                  const sel = (modal.unlock_package_ids || []).includes(p.id);
                  return (
                    <label key={p.id} data-testid={`code-pkg-${p.id}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input type="checkbox" checked={sel} onChange={(e) => {
                        const cur = new Set(modal.unlock_package_ids || []);
                        if (e.target.checked) cur.add(p.id); else cur.delete(p.id);
                        setModal({ ...modal, unlock_package_ids: [...cur] });
                      }} />
                      <span className="flex-1">{p.name}</span>
                      <span className="text-[#A19DB0] text-xs">${p.price}{p.hidden ? " · hidden" : ""}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[#A19DB0]/70 text-[11px] mt-1">Selected packages become hidden and are revealed only when a member enters this code.</p>
            </div>
          )}
          <AField label="Expiry (optional)" type="datetime-local" value={modal.expires_at} onChange={(e) => setModal({ ...modal, expires_at: e.target.value })} data-testid="code-expiry" />
          <AField label="Description (optional)" value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} data-testid="code-desc" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} data-testid="code-active" /> Active</label>
        </AdminModal>
      )}
    </div>
  );
}
